import { Injectable, inject } from '@angular/core';

import { OfferSource } from '@cds';
import { FixturePage, FixtureType, OfferCategory } from '@cds/betting-offer';
import { TagType } from '@cds/betting-offer/tags';
import { BatchRequest, FixtureCountsRequest, FixtureRequest, FixtureState, OfferMapping, SortByCriteria } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { FavouritesConfig } from '@frontend/sports/common/client-config-data-access';
import { CountItem, isCompetitionItem, isVirtualCompetitionGroupItem, sportModelMethods } from '@frontend/sports/common/core/data-access/sport-model';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { FavouriteType } from '@frontend/sports/favourites/core/feature';
import { FavouritesDashboardUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { StatisticsConfigService } from '@frontend/sports/statistics/core/feature';
import { UserLocationService } from '@frontend/sports/user/feature';
import { cloneDeep, differenceBy, entries, flatten, forIn, groupBy, intersectionBy, mergeWith, toInteger } from 'lodash-es';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';

import { FixtureTab } from '../competition-list/competition.models';
import { FavouritesDashboardRequestBuilderService } from './favourites-dashboard-request-builder.service';
import { FavouritedItems, FavouritesFixturesModel, FavouritesSectionModel, FiltersForRoute } from './favourites-dashboard.model';
import { FavouritesFilterService } from './favourites-filters.service';

export interface FixturesRequest {
    favouritedItems: StringDictionary<FavouritedItems[]>;
    take?: number;
    skip?: number;
    tabId?: number;
}

const SEPARATOR = '-';

@Injectable({ providedIn: 'root' })
export class FavouritesDashboardService {
    private bettingOffer = inject(BettingOfferApi);
    private config = inject(FavouritesConfig);
    private favouritesFilters = inject(FavouritesFilterService);
    private requestBuilderService = inject(FavouritesDashboardRequestBuilderService);
    private userLocationService = inject(UserLocationService);
    private statisticsConfigService = inject(StatisticsConfigService);

    private readonly v2OfferSources: { [key: string]: boolean } = {};

    constructor() {
        this.v2OfferSources[FavouriteType.FixtureV2] = true;
        this.v2OfferSources[FavouriteType.ParticipantV2] = true;
    }

    getTabData(tab: string): Observable<FavouritesFixturesModel> {
        return this.favouritesFilters.getList().pipe(
            switchMap((favourites) => {
                const filters = this.favouritesFilters.getFiltersForRoute(tab, favourites);
                const request = this.requestBuilderService.buildRequest(filters.currentRoute, filters.section);
                const fixtureFilters: StringDictionary<FavouritedItems[]> = request && request.fixtureFilters;
                const hasCount = request && !!request.fixturesCount;

                if (!hasCount) {
                    return of({
                        tabFilter: filters.tabFilter,
                        fixtures: [],
                        currentRoute: filters.currentRoute,
                        fixturesFilters: fixtureFilters,
                    });
                }

                if (this.isCompetitionsOrParticipants(filters.currentRoute)) {
                    return this.getTabDataCompetitionsOrParticipants(fixtureFilters, filters);
                }

                return this.getFixtures({ favouritedItems: fixtureFilters, take: this.config.initialSize }).pipe(
                    map((fixtures) => ({
                        tabFilter: filters.tabFilter,
                        fixtures: fixtures || [],
                        currentRoute: filters.currentRoute,
                        fixturesFilters: fixtureFilters,
                    })),
                );
            }),
        );
    }

    private isCompetitionsOrParticipants(route: string): boolean {
        return route === FavouritesDashboardUrlParam.Competitions || route === FavouritesDashboardUrlParam.Players;
    }

    private getTabDataCompetitionsOrParticipants(
        fixtureFilters: StringDictionary<FavouritedItems[]>,
        filters: FiltersForRoute,
    ): Observable<FavouritesFixturesModel> {
        return forkJoin([
            this.getFixtures({
                favouritedItems: fixtureFilters,
                take: this.config.initialSize,
                tabId: FixtureTab.Matches,
            }),
            this.getGridTabsCounts(filters.tabFilter.gridSections, filters.currentRoute === FavouritesDashboardUrlParam.Players),
        ]).pipe(
            map(([fixtures, counts]) => {
                return <FavouritesFixturesModel>{
                    tabFilter: filters.tabFilter,
                    fixtures: fixtures || [],
                    currentRoute: filters.currentRoute,
                    fixturesFilters: fixtureFilters,
                    gridSectionsCounts: counts || [],
                };
            }),
        );
    }

    getFixtures(request: FixturesRequest): Observable<StringDictionary<FixturePage>[] | undefined> {
        const favourites = entries(request.favouritedItems);
        if (!(favourites && favourites.length)) {
            return of([]);
        }
        const batchRequestPerSport: BatchRequest<FixtureRequest>[][] = favourites.map((sportFavourites) => {
            const sportId = sportFavourites[0];
            const requestPerSport = sportFavourites[1].map((favourite) =>
                this.buildBatchRequestPerSport(favourite, sportId, request.take, request.skip, request.tabId),
            );

            return requestPerSport;
        });
        const batchRequest = flatten(batchRequestPerSport);

        return this.bettingOffer.getFixtureListBatch(batchRequest).pipe(
            map((response) =>
                entries(response || {}).map((sportData) => {
                    const result: StringDictionary<FixturePage> = {};
                    sportData[1].fixtures = sportData[1].fixtures.filter(
                        (f) => f.taggedLocations.length === 0 || this.userLocationService.isInLocation(f.taggedLocations),
                    );
                    result[sportData[0]] = sportData[1];

                    return result;
                }),
            ),
        );
    }

    getGridTabsCounts(gridSections: FavouritesSectionModel[], requestPlayers: boolean): Observable<StringDictionary<CountItem[]>[] | undefined> {
        const sections = gridSections.filter((section) => section.gridHeaderPills?.length);

        if (!sections.length) {
            return of([]);
        }

        let request: BatchRequest<FixtureCountsRequest>[];

        if (requestPlayers) {
            const requestsPerPlayers = sections.map((section) => {
                const gridHeaderPills = section.gridHeaderPills!;
                const sportId = section.id;

                const playerReqs = gridHeaderPills
                    .filter((p) => p.id !== '-1')
                    .map((p) =>
                        this.buildBatchCountsRequest(
                            {
                                type: p.type!,
                                itemIds: [p.id],
                            },
                            sportId,
                            this.getPlayerSectionId(sportId, p.id, p.type),
                        ),
                    );

                const allPill = gridHeaderPills.find((p) => p.id === '-1');
                if (allPill?.allItemIds) {
                    const diffBatchesPerType = allPill.allItemIds.length > 1;
                    const allReq = allPill.allItemIds.map((item) =>
                        this.buildBatchCountsRequest(
                            { type: item.type, itemIds: item.itemIds },
                            sportId,
                            diffBatchesPerType ? `${sportId}${SEPARATOR}${item.type}` : sportId,
                        ),
                    );

                    return playerReqs.concat(allReq);
                }

                return playerReqs;
            });

            request = flatten(requestsPerPlayers);
        } else {
            const requestsPerLeagues = sections.map((section) => {
                const sportId = section.id;
                const firstPill = section.gridHeaderPills![0];
                const items = firstPill.allItemIds || [
                    {
                        type: firstPill.type || FavouriteType.League,
                        itemIds: [firstPill.id],
                    },
                ];

                return items.map((item) => this.buildBatchCountsRequest(item, sportId));
            });
            request = flatten(requestsPerLeagues);
        }

        return this.bettingOffer.getBatchCounts(request).pipe(
            map((response) => {
                const counts = entries(response || {}).map((sports) => {
                    const countItems = sportModelMethods.fromTag(sports[1]);

                    return { [sports[0]]: countItems };
                });

                if (requestPlayers) {
                    this.processPlayersCountsResponse(counts);
                }

                return counts;
            }),
        );
    }

    private processPlayersCountsResponse(counts: StringDictionary<CountItem[]>[]): void {
        const rawValues = flatten(counts.map((c) => entries(c)));
        const grouped = groupBy(rawValues, (c) => {
            const parts = c[0].split(SEPARATOR);
            if (parts.length === 2) {
                return parts[0];
            }

            return c[0];
        });
        forIn(grouped, (cnts, groupId) => {
            let total: CountItem;
            const items = flatten(cnts.map((c) => c[1]));
            if (items.length > 1) {
                const groupCounts = items.reduce((aggr, c) => {
                    if (!total) {
                        total = cloneDeep(aggr);
                    }
                    this.aggregateCountItem([total], [c]);

                    return total;
                });
                const groupResult: StringDictionary<CountItem[]> = {};
                groupResult[groupId] = [groupCounts];
                counts.push(groupResult);
            }
        });
        const rawCounts = flatten(counts.map((c) => entries(c)));
        const sportCounts = rawCounts.filter((cnt) => cnt[0].indexOf(SEPARATOR) < 0);
        for (const sportCount of sportCounts) {
            const playerCompetitions = flatten(
                rawCounts
                    .filter((cnt) => {
                        const parts = cnt[0].split(SEPARATOR);

                        return parts[0] === sportCount[0] && parts.length === 3;
                    })
                    .map((c) => this.getCompetitionItems(c[1])),
            );
            this.setCompetitionOrder(sportCount[1], playerCompetitions);
        }
    }

    private setCompetitionOrder(items: CountItem[], competitions: number[]): void {
        for (const item of items) {
            if (item.children.length > 0) {
                this.setCompetitionOrder(item.children, competitions);
            } else if (isCompetitionItem(item) || isVirtualCompetitionGroupItem(item)) {
                const order = competitions.indexOf(item.id);
                if (order !== -1) {
                    item.order = order;
                }
            }
        }
    }

    private getCompetitionItems(countItems: CountItem[]): number[] {
        const result: number[] = [];
        for (const countItem of countItems) {
            if (countItem.children.length > 0) {
                result.push(...this.getCompetitionItems(countItem.children));
            } else if (isCompetitionItem(countItem) || isVirtualCompetitionGroupItem(countItem)) {
                result.push(countItem.id);
            }
        }

        return result;
    }

    private aggregateCountItem(totalCountItems: CountItem[], countItems: CountItem[]): void {
        totalCountItems.forEach((total) => {
            const newItem = countItems.find((ci) => ci.id === total.id && ci.type === total.type);
            if (newItem) {
                mergeWith(total.counts, newItem.counts, this.sumItemCounts);

                if (total.children.length) {
                    const newChildren = differenceBy(newItem.children, total.children, (countItem) => `${countItem.id}-${countItem.type}`);
                    total.children.push(...newChildren);

                    const existingChildren = intersectionBy(total.children, newItem.children, (countItem) => `${countItem.id}-${countItem.type}`);
                    if (existingChildren.length) {
                        this.aggregateCountItem(existingChildren, newItem.children);
                    }
                }
            }
        });
    }

    private sumItemCounts(total: number, count: number): number {
        return total + count;
    }

    getPlayerSectionId(sportId: string, id: string, type?: FavouriteType): string {
        return `${sportId}${SEPARATOR}${id}${SEPARATOR}${type}`;
    }

    private getOfferSource(favouriteType: FavouriteType): OfferSource | undefined {
        if (favouriteType === FavouriteType.League) {
            return;
        }

        return this.v2OfferSources[favouriteType] ? OfferSource.V2 : OfferSource.V1;
    }

    private buildBatchRequestPerSport(
        favourite: FavouritedItems,
        sportId: string,
        take?: number,
        skip?: number,
        tabId?: number,
    ): BatchRequest<FixtureRequest> {
        {
            const itemIds = favourite.itemIds.join(',');
            const request: BatchRequest<FixtureRequest> = {
                batchId: sportId,
                request: {
                    sportIds: sportId,
                    take,
                    skip,
                    fixtureTypes: FixtureType.Standard,
                    offerSource: this.getOfferSource(favourite.type),
                    state: FixtureState.Latest,
                    offerMapping: OfferMapping.Filtered,
                    offerCategories: this.getOfferCategories(tabId),
                    sortBy: this.getSorting(tabId),
                    fixtureCategories: this.getFixtureCategories(tabId),
                    excludeLocationFilter: true,
                    statisticsModes: this.statisticsConfigService.getStatisticsModesBySportId(toInteger(sportId)),
                },
            };
            if (favourite.type === FavouriteType.League) {
                request.request.competitionIds = itemIds;
            }

            if (favourite.type === FavouriteType.Participant || favourite.type === FavouriteType.ParticipantV2) {
                request.request.fixtureParticipantIds = itemIds;
            }

            if (favourite.type === FavouriteType.Fixture || favourite.type === FavouriteType.FixtureV2) {
                request.request.fixtureIds = itemIds;
            }

            return request;
        }
    }

    private buildBatchCountsRequest(favourite: FavouritedItems, sportId: string, batchId?: string): BatchRequest<FixtureCountsRequest> {
        {
            const itemIds = favourite.itemIds.join(',');
            const request: BatchRequest<FixtureCountsRequest> = {
                batchId: batchId || sportId,
                request: {
                    sportIds: sportId,
                    state: FixtureState.Latest,
                    fixtureTypes: FixtureType.Standard,
                    offerSource: this.getOfferSource(favourite.type),
                    sortBy: SortByCriteria.Tags,
                    tagTypes: [
                        TagType.Sport,
                        TagType.Region,
                        TagType.Tournament,
                        TagType.Competition,
                        TagType.VirtualCompetition,
                        TagType.VirtualCompetitionGroup,
                    ].join(','),
                    extendedTags: [
                        TagType.Sport,
                        TagType.Region,
                        TagType.Tournament,
                        TagType.Competition,
                        TagType.VirtualCompetition,
                        TagType.VirtualCompetitionGroup,
                    ].join(','),
                    statisticsModes: this.statisticsConfigService.getStatisticsModesBySportId(toInteger(sportId)),
                },
            };
            if (favourite.type === FavouriteType.League) {
                request.request.competitionIds = itemIds;
            }

            if (favourite.type === FavouriteType.Participant || favourite.type === FavouriteType.ParticipantV2) {
                request.request.fixtureParticipantIds = itemIds;
            }

            return request;
        }
    }

    private getSorting(tabId?: number): SortByCriteria {
        return tabId && (tabId === FixtureTab.Outrights || tabId === FixtureTab.Specials)
            ? SortByCriteria.QueryCompetitionIds
            : SortByCriteria.StartDate;
    }

    private getOfferCategories(tabId?: number): OfferCategory {
        if (tabId === FixtureTab.Outrights) {
            return OfferCategory.Outrights;
        }
        if (tabId === FixtureTab.Specials) {
            return OfferCategory.Specials;
        }

        return OfferCategory.Gridable;
    }

    private getFixtureCategories(tabId?: number): string {
        if (tabId === FixtureTab.Outrights) {
            return OfferCategory.Outrights;
        }
        if (tabId === FixtureTab.Specials) {
            return OfferCategory.Specials;
        }
        if (tabId === FixtureTab.Matches) {
            return [OfferCategory.Gridable, OfferCategory.NonGridable, OfferCategory.Other].join(',');
        }

        return '';
    }
}
